import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bffApiClient } from '@api/bff/client';

export type EquipmentSummary = {
  companyName: string;
  id: string;
  globalId: string;
  serialNumber: string | null;
  description: string | null;
  manufacturer: string | null;
  modelNumber: string | null;
  type: string | null;
  subtype: string | null;
};

export type CreateEquipmentRequest = {
  templateName: string;
  companyName: string;
  gageSn: string;
  subtype?: string | null;
};

const EQUIPMENT_QUERY_KEY = 'equipment';

export const equipmentKeys = {
  companies: (search: string) =>
    [EQUIPMENT_QUERY_KEY, 'companies', search] as const,
  byCompany: (companyName: string) =>
    [EQUIPMENT_QUERY_KEY, 'byCompany', companyName] as const
};

export const useCompaniesQuery = (search: string, enabled = true) => {
  return useQuery<string[], Error>({
    queryKey: equipmentKeys.companies(search),
    queryFn: async () => {
      const response = await bffApiClient.get<string[]>(
        '/api/equipment/companies',
        { params: search ? { search } : undefined }
      );
      return response.data;
    },
    enabled,
    staleTime: 60_000
  });
};

export const useCompanyEquipmentQuery = (
  companyName: string | null,
  enabled = true
) => {
  return useQuery<EquipmentSummary[], Error>({
    queryKey: equipmentKeys.byCompany(companyName ?? ''),
    queryFn: async () => {
      const response = await bffApiClient.get<EquipmentSummary[]>(
        '/api/equipment/bulk',
        { params: { companyName } }
      );
      return response.data;
    },
    enabled: enabled && !!companyName,
    staleTime: 30_000
  });
};

export const useCreateEquipment = () => {
  const queryClient = useQueryClient();

  return useMutation<EquipmentSummary, Error, CreateEquipmentRequest>({
    mutationFn: async payload => {
      const response = await bffApiClient.post<EquipmentSummary>(
        '/api/equipment',
        payload
      );
      return response.data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: equipmentKeys.byCompany(data.companyName)
      });
    }
  });
};
